import React, { useRef, useState } from 'react';
import { Popover } from '@mui/material';
import { type Argument, cs } from '@bbkAdminUtils/css-utils';
import _ from 'lodash';
import RadioButton from '@bbkAdminComponents/switches/radio-button/radio-button';
import Icons from '@bbkAdminUtils/icons';
import {
  type Period,
  type TimePickerStructure,
  type TimeRangesEnum,
} from '@bbkAdminUtils/time-periods';
import {
  isCustomTimePeriod,
  TimePeriods,
  TimeRanges,
} from '@bbkAdminUtils/time-periods';
import { DateRangePicker } from '@bbkAdminComponents/date-range-picker/date-range-picker';
import { usePlans } from '@bbkAdminUtils/plans';
import { TippyTooltip } from '@bbkAdminComponents/tooltips/tippy-tooltip';
import { useStrings } from '@bbkAdminUtils/strings';
import { useSelectDatePicker } from '@bbkAdminRedux/rtkq/datepicker-persist.slice';

import './styles/time_picker.scss';

type Props = {
  timePicker?: TimePickerStructure;
  timeValues?: Period[];
  save?: (timeRange: TimePickerStructure) => void;
  className?: Argument;
  hidePicker?: boolean;
  disableButton?: boolean;
  readOnly?: boolean;
};

// Time Picker is the whole time picking component
// Includes the pre-baked times && the date range pickers

const TimePicker: React.FC<Props> = (props) => {
  const timePickerState = useSelectDatePicker();
  const strings = useStrings();
  const { isStarterFreePlan } = usePlans();
  const {
    className,
    timeValues = TimePeriods,
    hidePicker = false,
    disableButton,
    readOnly,
    save,
    timePicker: timePickerProp,
  } = props;
  const timePicker = timePickerProp || timePickerState;
  const { timeAgoKey, startDate, endDate } = timePicker;
  const timeAgoText = timeValues.find((r) => r.value === timeAgoKey)?.name;
  const popoverRef = useRef<HTMLButtonElement>(null);
  const [disabled, setDisabled] = useState(false);

  const handleOpen = (): void => setDisabled(true);
  const handleClose = (): void => setDisabled(false);

  const setDateRange = (dateRange: TimePickerStructure): void => {
    if (save) {
      save(dateRange);
    }
  };

  const handleCustomRange = ([start, end]: [Date, Date]): void => {
    setDateRange({ timeAgoKey: 'CUSTOM', startDate: start, endDate: end });
  };

  const handleItemClick = (item: Period): void => {
    const tak = item.value as TimeRangesEnum;
    const range = TimeRanges[tak];

    // if custom, need to grab the existing time
    if (isCustomTimePeriod(tak)) {
      handleCustomRange([startDate, endDate]);
    } else {
      setDateRange({ timeAgoKey: tak, startDate: range[0], endDate: range[1] });
    }
    handleClose();
  };

  const renderMenuItems = (): React.ReactNode => (
    <div className="bbk-po-menu no-pad">
      <div className="bbk-dp-popover-inner-container">
        {timeValues.map((timeValue) => {
          const radioButton = (
            <RadioButton
              label={_.upperFirst(timeValue.name)}
              checked={timeAgoKey === timeValue.value}
              disabled={timeValue.disabled}
              handleChange={() => handleItemClick(timeValue)}
              classes="black small"
              labelClass={cs({
                'label-semibold': timeAgoKey === timeValue.value,
              })}
            />
          );
          const radioButtonWithTooltip =
            isStarterFreePlan && timeValue.disabled ? (
              <TippyTooltip
                content={strings.UPGRADE_PLAN({
                  description: strings.UPGRADE_TO_STARTER_PREMIUM_DESC,
                })}
                tippyOptions={{
                  appendTo: () => document.body,
                  zIndex: 1400,
                  hideOnClick: true,
                  placement: 'right',
                }}
              >
                <div>{radioButton}</div>
              </TippyTooltip>
            ) : (
              radioButton
            );
          return (
            <div
              key={timeValue.name}
              className={cs('bbk-dp-item', {
                'bbk-dp-selected': timeValue.selected,
              })}
            >
              {radioButtonWithTooltip}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={cs('time-picker', className)}>
      {!hidePicker && (
        <button
          className="timeAgoButton"
          onClick={handleOpen}
          ref={popoverRef}
          type="button"
          disabled={disableButton}
        >
          <span>
            {Icons.calendar}
            {_.capitalize(timeAgoText)}
          </span>
          {Icons.doubleChevron}
        </button>
      )}
      <DateRangePicker
        loading={disableButton}
        disabled={readOnly || disableButton}
        startDate={startDate}
        endDate={endDate}
        onRangeChange={handleCustomRange}
      />
      <Popover
        classes={{
          paper: 'popover popover-time-picker bbk-date-picker-popover',
        }}
        onClose={handleClose}
        anchorEl={popoverRef.current}
        open={disabled}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        <div>{renderMenuItems()}</div>
      </Popover>
    </div>
  );
};

export default TimePicker;
